import {
  MdKeyboardArrowDown,
  MdKeyboardArrowUp,
  MdPeopleAlt,
  MdSubdirectoryArrowRight,
  MdViewColumn,
} from '@teamworksdev/icons';
import { useTranslation } from 'react-i18next';
import { useViewer } from '../../../../context/viewer';
import { usePlanContext } from '../../context/PlanContext';
import { getTeamColor, hexWithAlpha } from './cross-team-colors';

const ROW_HEIGHT = 48;
const SUBTRACK_ROW_HEIGHT = 40;
// Distance from the inside of the team-color strip to the start of row content.
// Combined with the 8px color strip, total content offset from panel edge is 24px,
// matching the Figma `pl-[24px]` on the team group container.
const ROW_PADDING_LEFT_PX = 16;
const SUBTRACK_INDENT_PX = 40;
// Background tint opacity — keep low so the team color is recognizable but
// the row content stays legible.
const TEAM_TINT_OPACITY = 0.15;

export function AdditionalTeamsTracks() {
  const { t } = useTranslation();
  const { showAdditionalTeams, additionalTeamsData } = usePlanContext();

  if (!showAdditionalTeams || additionalTeamsData.length === 0) {
    return null;
  }

  return (
    <div className="flex flex-col flex-shrink-0">
      {/* Divider lives on this row (explicit height), not the auto-height
          wrapper — see the matching comment in ArchivedTracksSection. */}
      <div
        className="flex flex-row w-full pl-6 pr-3 items-center border-b border-t-4"
        style={{ height: ROW_HEIGHT }}
      >
        <span className="flex-grow font-semibold text-sm truncate">
          {t('additionalTeams', { teamCount: additionalTeamsData.length })}
        </span>
      </div>
      {additionalTeamsData.map((team, index) => (
        <TeamGroup
          key={team.teamId}
          teamId={team.teamId}
          teamName={team.teamName}
          tracks={team.tracks}
          color={getTeamColor(index)}
        />
      ))}
    </div>
  );
}

function TeamGroup({
  teamId,
  teamName,
  tracks,
  color,
}: {
  teamId: string;
  teamName: string;
  tracks: ReadonlyArray<{
    id: string;
    name: string;
    parentTrackId: string | null;
  }>;
  color: string;
}) {
  const { t } = useTranslation();
  // Mirrors BlockPanel: the per-team Phases row is removed when the viewer
  // can't see phases so the two panels stay aligned row-for-row.
  const { hasPermissions } = useViewer();
  const canViewPhases = hasPermissions('plan.phases', 'view');
  const { collapsedCrossTeamTeamIds, toggleCrossTeamTeamCollapsed } =
    usePlanContext();
  const collapsed = collapsedCrossTeamTeamIds.has(teamId);
  const setCollapsed = () => toggleCrossTeamTeamCollapsed(teamId);

  const tintStyle = {
    backgroundColor: hexWithAlpha(color, TEAM_TINT_OPACITY),
    borderLeft: `8px solid ${color}`,
  };
  const parentTracks = tracks.filter((track) => track.parentTrackId === null);

  return (
    <div className="flex flex-col" style={tintStyle}>
      <button
        type="button"
        onClick={setCollapsed}
        className="flex flex-row w-full pr-3 items-center text-left bg-transparent"
        style={{ height: ROW_HEIGHT, paddingLeft: ROW_PADDING_LEFT_PX }}
        aria-expanded={!collapsed}
      >
        <span className="flex-grow font-semibold text-sm truncate">
          {teamName}
        </span>
        {collapsed ? (
          <MdKeyboardArrowDown size={18} className="text-gray-500" />
        ) : (
          <MdKeyboardArrowUp size={18} className="text-gray-500" />
        )}
      </button>
      {!collapsed && (
        <>
          {canViewPhases && (
            <div
              className="flex flex-row w-full pr-3 items-center border-t"
              style={{ height: ROW_HEIGHT, paddingLeft: ROW_PADDING_LEFT_PX }}
            >
              <MdViewColumn size={18} className="mr-2 text-gray-500" />
              <span className="flex-grow font-semibold text-sm truncate">
                {t('phases')}
              </span>
            </div>
          )}
          {parentTracks.map((track) => (
            <ParentTrackRow
              key={track.id}
              track={track}
              subtracks={tracks.filter((s) => s.parentTrackId === track.id)}
            />
          ))}
        </>
      )}
    </div>
  );
}

function ParentTrackRow({
  track,
  subtracks,
}: {
  track: { id: string; name: string };
  subtracks: ReadonlyArray<{ id: string; name: string }>;
}) {
  const { collapsedCrossTeamTrackIds, toggleCrossTeamTrackCollapsed } =
    usePlanContext();
  const collapsed = collapsedCrossTeamTrackIds.has(track.id);
  const hasSubtracks = subtracks.length > 0;

  const rowClassName =
    'flex flex-row w-full pr-3 items-center text-left bg-transparent';
  const rowStyle = {
    height: ROW_HEIGHT,
    paddingLeft: ROW_PADDING_LEFT_PX,
  };

  return (
    <>
      {hasSubtracks ? (
        <button
          type="button"
          className={rowClassName}
          style={rowStyle}
          onClick={() => toggleCrossTeamTrackCollapsed(track.id)}
          aria-expanded={!collapsed}
        >
          <MdPeopleAlt size={18} className="mr-2 text-gray-500" />
          <span className="flex-grow font-semibold text-sm truncate">
            {track.name}
          </span>
          {collapsed ? (
            <MdKeyboardArrowDown size={18} className="text-gray-500" />
          ) : (
            <MdKeyboardArrowUp size={18} className="text-gray-500" />
          )}
        </button>
      ) : (
        <div className={rowClassName} style={rowStyle}>
          <MdPeopleAlt size={18} className="mr-2 text-gray-500" />
          <span className="flex-grow font-semibold text-sm truncate">
            {track.name}
          </span>
        </div>
      )}
      {!collapsed &&
        subtracks.map((sub) => (
          <div
            key={sub.id}
            className="flex flex-row w-full items-center"
            style={{
              height: SUBTRACK_ROW_HEIGHT,
              paddingLeft: SUBTRACK_INDENT_PX,
            }}
          >
            <MdSubdirectoryArrowRight
              size={18}
              className="mr-2 text-gray-500"
            />
            <span className="flex-grow font-semibold text-xs truncate">
              {sub.name}
            </span>
          </div>
        ))}
    </>
  );
}
